/**
 * ⭐ ВАРИАНТЫ «ВЫБОРА» · ступени 1, 1а и 2 — окно М4 (29.07).
 *
 * Шестой модуль витрины с настоящей логикой (после `grammar` · `audio` · `build` ·
 * `ladder` · `gap`). Проверяется запуском: `cd Lang/web && node tools/check-ladder.mjs`.
 *
 * Раньше эти пятнадцать строк жили внутри `screens/session.ts` и проверить их было нечем.
 * Вынесены сюда БЕЗ изменения поведения EN→RU — поменялась только обратная сторона:
 *
 *   • **EN→RU (ступени 1 и 1а)** — три неверных РУССКИХ перевода лежат готовыми в базе
 *     (`lexicon.distractors`, проход B). Как было, так и осталось.
 *   • ⭐ **RU→EN (ступень 2)** — три неверных АНГЛИЙСКИХ слова теперь собирает ЯДРО из
 *     словаря самого человека (`lexicon.english_distractors` → поле `distractors_en`):
 *     та же часть речи, тот же уровень CEFR, ближайший ранг частотности, ⛔ не форма того
 *     же слова и ⛔ не слово с тем же значением. До М4 витрина брала соседей ПО КОЛОДЕ
 *     подхода — рядом с «дом» вставали «бежать» и «синий», и выбор ничему не учил.
 *
 * Колода осталась ЗАПАСНОЙ дорогой: ядро не прислало вариантов (старый ответ API, слово без
 * знания) — собираем как раньше, лишь бы человек не остался перед пустым экраном.
 *
 * ⛔ Ни DOM, ни сети, ни SRS: только строки.
 */

import type { Card } from "./api";

export interface ChoiceSpec {
  /** правильный ответ: перевод (EN→RU) или само слово (RU→EN) */
  correct: string;
  /** что показываем кнопками — правильный + три неверных, перемешаны стабильно */
  options: string[];
  /** откуда взялись неверные: готовые из базы · собранные ядром · соседи по колоде */
  source: "distractors" | "core" | "deck";
}

/** Сколько всего кнопок на экране (правильный + три неверных). */
export const OPTIONS = 4;

/** Перемешивание с постоянным зерном: перерисовка не двигает кнопки под пальцем. */
export function shuffle(list: string[], seed: number): string[] {
  const out = list.slice();
  let s = seed * 9301 + 49297;
  for (let i = out.length - 1; i > 0; i--) {
    s = (s * 9301 + 49297) % 233280;
    const j = Math.floor((s / 233280) * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/**
 * Варианты для карточки-выбора. `pool` — слова (или переводы) соседних карточек подхода:
 * ими добираем, если готовых не хватило. Не набралось трёх неверных — вернём null, и
 * занятие честно покажет карточку «Досье» (пустых экранов человек видеть не должен).
 */
export function choiceSpec(c: Card, pool: string[] = []): ChoiceSpec | null {
  const enRu = c.direction === "en_ru";
  const correct = (enRu ? c.translation : c.word) || "";
  if (!correct.trim()) return null;

  const wrong: string[] = [];
  const add = (value?: string | null): void => {
    const v = (value || "").trim();
    if (!v || wrong.length >= OPTIONS - 1) return;
    if (v.toLowerCase() === correct.toLowerCase()) return;
    if (wrong.some((p) => p.toLowerCase() === v.toLowerCase())) return;
    wrong.push(v);
  };

  // Сперва то, что подготовлено ЯДРОМ: русские дистракторы прохода B либо английские
  // соседи по словарю. Колода — только чтобы добрать хвост.
  const ready = enRu ? c.distractors : (c.distractors_en || []);
  ready.forEach(add);
  const fromCore = wrong.length;
  for (const p of pool) add(p);

  if (wrong.length < OPTIONS - 1) return null;
  return {
    correct,
    options: shuffle([correct, ...wrong], c.word_id),
    source: fromCore === 0 ? "deck" : enRu ? "distractors" : "core",
  };
}
